import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CartContext } from "../context/CartContext";
import api from "../api/axios";
import Footer from "../components/Footer";

const inr = (amount) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount);

const CARD_PALETTE = {
  Woody:  { bg: "#e8d9c4", accent: "#9b6e3a" },
  Floral: { bg: "#f2e0e8", accent: "#c4607a" },
  Fresh:  { bg: "#d8eee6", accent: "#3a8a68" },
  Spice:  { bg: "#f0e0cc", accent: "#c47a3a" },
};

// ✅ Fix — handles both {id, name} object (API) and plain string (dummy)
const getCategoryName = (category) => {
  if (!category) return "";
  if (typeof category === "object") return category.name || "";
  return String(category);
};

export default function Cart() {
  const { cartItems, cartCount, removeFromCart, updateQuantity, clearCart } = useContext(CartContext);
  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.getItem("token");

  const [shippingCharge,    setShippingCharge]    = useState(99);
  const [freeShippingAbove, setFreeShippingAbove] = useState(999);

  useEffect(() => {
    api.get("settings/")
      .then(res => {
        const data = res.data;
        if (data) {
          setShippingCharge(parseFloat(data.shipping_charge) || 99);
          setFreeShippingAbove(parseFloat(data.free_shipping_above) || 999);
        }
      })
      .catch(() => {});
  }, []);

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: "#faf7f2", paddingTop: "64px", fontFamily: "Georgia, serif" }}>
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔒</div>
        <h2 style={{ fontSize: "22px", color: "#1a1a1a", marginBottom: "8px" }}>Please log in</h2>
        <p style={{ color: "#888", fontSize: "13px", marginBottom: "24px" }}>You need to be logged in to view your cart.</p>
        <button onClick={() => navigate("/login")} className="rounded-full px-8 py-3 text-xs font-semibold tracking-widest uppercase"
          style={{ background: "#e07b39", color: "#fff", border: "none", cursor: "pointer", fontFamily: "Georgia, serif", letterSpacing: "0.1em" }}>
          Log In
        </button>
      </div>
    );
  }

  const subtotal = cartItems.reduce((sum, item) => sum + parseFloat(item.price || 0) * item.quantity, 0);

  const shipping = subtotal >= freeShippingAbove && freeShippingAbove > 0 ? 0 : shippingCharge;
  const total    = subtotal + shipping;

  return (
    <div style={{ background: "#faf7f2", minHeight: "100vh", paddingTop: "64px" }}>
      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #f0ebe3", padding: "40px 6% 32px", textAlign: "center" }}>
        <p style={{ fontFamily: "Georgia, serif", color: "#e07b39", fontSize: "11px", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: "8px" }}>
          Your Selections
        </p>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: "clamp(24px, 3vw, 36px)", color: "#1a1a1a", fontWeight: "700" }}>
          Shopping Cart
        </h1>
        <p style={{ fontFamily: "Georgia, serif", color: "#aaa", fontSize: "13px", marginTop: "8px" }}>
          {cartCount} item{cartCount !== 1 ? "s" : ""} in your cart
        </p>
      </div>

      {/* Empty state */}
      {cartItems.length === 0 ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "100px 24px", textAlign: "center" }}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d0c8b8" strokeWidth="1.2" style={{ marginBottom: "20px" }}>
            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" />
          </svg>
          <h3 style={{ fontFamily: "Georgia, serif", color: "#1a1a1a", fontSize: "20px", marginBottom: "8px" }}>Your cart is empty</h3>
          <p style={{ fontFamily: "Georgia, serif", color: "#aaa", fontSize: "13px", marginBottom: "28px" }}>Discover our handcrafted candle collection.</p>
          <button onClick={() => navigate("/products")} className="rounded-full px-8 py-3 text-xs font-semibold tracking-widest uppercase"
            style={{ background: "#e07b39", color: "#fff", border: "none", cursor: "pointer", fontFamily: "Georgia, serif", letterSpacing: "0.1em" }}>
            Shop Candles
          </button>
        </div>
      ) : (
        <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "40px 6%", display: "grid", gridTemplateColumns: "1fr 340px", gap: "32px", alignItems: "start" }}
          className="cart-grid">

          {/* ── Left: Cart Items ─────────────────────────────────────────── */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ fontFamily: "Georgia, serif", fontSize: "16px", color: "#1a1a1a", fontWeight: "700" }}>Cart Items</h2>
              <button onClick={clearCart} style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#e07b39", background: "none", border: "none", cursor: "pointer" }}>
                Clear All
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              {cartItems.map((item) => {
                // ✅ Fix — extract name string before palette lookup
                const categoryName = getCategoryName(item.category);
                const palette = CARD_PALETTE[categoryName] || { bg: "#f0ebe3", accent: "#888" };
                return (
                  <CartItem
                    key={item.id}
                    item={item}
                    categoryName={categoryName}
                    palette={palette}
                    onRemove={() => removeFromCart(item.id)}
                    onUpdateQty={(q) => updateQuantity(item.id, q)}
                  />
                );
              })}
            </div>

            <button onClick={() => navigate("/products")} style={{ marginTop: "28px", fontFamily: "Georgia, serif", fontSize: "12px", color: "#888", background: "none", border: "none", cursor: "pointer" }}>
              ← Continue Shopping
            </button>
          </div>

          {/* ── Right: Order Summary ─────────────────────────────────────── */}
          <div style={{ background: "#fff", borderRadius: "16px", padding: "28px", border: "1px solid #f0ebe3", position: "sticky", top: "84px" }}>
            <h2 style={{ fontFamily: "Georgia, serif", fontSize: "16px", color: "#1a1a1a", fontWeight: "700", marginBottom: "24px" }}>
              Order Summary
            </h2>

            <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "20px" }}>
              <SummaryRow label="Subtotal" value={inr(subtotal)} />
              <SummaryRow
                label="Shipping"
                value={shipping === 0 ? <span style={{ color: "#3a8a68", fontFamily: "Georgia, serif", fontSize: "13px" }}>Free</span> : inr(shipping)}
              />
              {shipping > 0 && (
                <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa", marginTop: "-6px" }}>
                  Free shipping on orders above ₹{freeShippingAbove}
                </p>
              )}
            </div>

            <div style={{ borderTop: "1px solid #f0ebe3", paddingTop: "16px", marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "15px", fontWeight: "700", color: "#1a1a1a" }}>Total</span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "18px", fontWeight: "700", color: "#e07b39" }}>{inr(total)}</span>
            </div>

            <button
              onClick={() => navigate("/checkout")}
              className="w-full rounded-full py-3 text-xs font-semibold tracking-widest uppercase transition-all duration-200"
              style={{ background: "linear-gradient(135deg, #e07b39, #c96a28)", color: "#fff", border: "none", cursor: "pointer", fontFamily: "Georgia, serif", letterSpacing: "0.1em", boxShadow: "0 4px 16px rgba(224,123,57,.35)" }}
              onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 6px 24px rgba(224,123,57,.5)")}
              onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 4px 16px rgba(224,123,57,.35)")}
            >
              Proceed to Checkout
            </button>

            <div style={{ display: "flex", justifyContent: "center", gap: "16px", marginTop: "20px" }}>
              {["🔒 Secure", "♻️ Eco-packed", "🕯️ Handmade"].map((b) => (
                <span key={b} style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#bbb" }}>{b}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      <Footer />

      <style>{`
        @media (max-width: 768px) {
          .cart-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

// ── Cart Item Row ─────────────────────────────────────────────────────────────
function CartItem({ item, categoryName, palette, onRemove, onUpdateQty }) {
  const lineTotal = parseFloat(item.price || 0) * item.quantity;
  const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

  return (
    <div style={{ background: "#fff", borderRadius: "14px", padding: "16px", display: "flex", gap: "16px", alignItems: "center", border: "1px solid #f0ebe3" }}>
      {/* Image / placeholder */}
      <div style={{ width: "72px", height: "72px", borderRadius: "10px", background: palette.bg, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px", overflow: "hidden" }}>
        {item.image
          ? <img src={item.image} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : "🕯️"
        }
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "Georgia, serif", fontSize: "14px", fontWeight: "600", color: "#1a1a1a", marginBottom: "2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {item.name}
        </div>
        {/* ✅ Fix — use categoryName string, not item.category object */}
        <div style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa", marginBottom: "10px" }}>
          {categoryName || "Candle"} · {inr(parseFloat(item.price))} each
        </div>

        {/* Qty controls */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", border: "1.5px solid #e8dfd0", borderRadius: "20px", overflow: "hidden" }}>
            <button onClick={() => onUpdateQty(item.quantity - 1)}
              style={{ width: "30px", height: "28px", background: "none", border: "none", cursor: "pointer", fontSize: "15px", color: "#666", display: "flex", alignItems: "center", justifyContent: "center" }}>
              −
            </button>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "600", color: "#1a1a1a", minWidth: "24px", textAlign: "center" }}>
              {item.quantity}
            </span>
            <button onClick={() => onUpdateQty(item.quantity + 1)}
              style={{ width: "30px", height: "28px", background: "none", border: "none", cursor: "pointer", fontSize: "15px", color: "#666", display: "flex", alignItems: "center", justifyContent: "center" }}>
              +
            </button>
          </div>
          <button onClick={onRemove} style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#e07b39", background: "none", border: "none", cursor: "pointer" }}>
            Remove
          </button>
        </div>
      </div>

      {/* Line total */}
      <div style={{ fontFamily: "Georgia, serif", fontSize: "15px", fontWeight: "700", color: "#1a1a1a", flexShrink: 0 }}>
        {inr(lineTotal)}
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#888" }}>{label}</span>
      <span style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#1a1a1a", fontWeight: "600" }}>{value}</span>
    </div>
  );
}